/**
 * Data hook for fetching chat history
 * Separates GraphQL operations from UI components
 */

import type { ChatHistoryResponse, EditChatConfig } from '@choco-admin/types';
import { useCallback, useEffect, useState, useRef } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useChatHistoryDataQuery } from '../../../generated';
import { mapToChatHistoryResponse } from '../utils';

export interface UseChatHistoryDataResponse {
  historyData: ChatHistoryResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useChatHistoryData = (
  chatId: string,
  config: EditChatConfig = {}
): UseChatHistoryDataResponse => {
  const { onError, captureException } = config;
  const [historyData, setHistoryData] = useState<ChatHistoryResponse | null>(
    null
  );
  const [error, setError] = useState<Error | null>(null);
  const { addNotification } = useNotification();

  // Use refs to store callback functions to avoid dependency issues
  const onErrorRef = useRef(onError);
  const captureExceptionRef = useRef(captureException);

  // Update refs when callbacks change
  useEffect(() => {
    onErrorRef.current = onError;
    captureExceptionRef.current = captureException;
  }, [onError, captureException]);

  const {
    data,
    loading,
    error: gqlError,
    refetch: gqlRefetch,
  } = useChatHistoryDataQuery({
    variables: {
      id: chatId,
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  // Map GraphQL data to domain types
  useEffect(() => {
    if (data?.messages) {
      try {
        const mappedData = mapToChatHistoryResponse(data.messages);
        setHistoryData(mappedData);
        setError(null);
      } catch (mappingError) {
        const err = new Error(
          `Failed to map chat history data: ${mappingError}`
        );
        setError(err);
        onErrorRef.current?.(err);
        captureExceptionRef.current?.(err);
      }
    }
  }, [data]);

  // Handle GraphQL errors
  useEffect(() => {
    if (gqlError) {
      const err = new Error(
        `Failed to fetch chat history: ${gqlError.message}`
      );
      setError(err);
      onErrorRef.current?.(err);
      captureExceptionRef.current?.(err);

      addNotification({
        title: 'Error Loading Chat History',
        message: 'Failed to load chat history. Please try again.',
        type: 'error',
      });
    }
  }, [gqlError, addNotification]);

  const refetch = useCallback(async () => {
    try {
      setError(null);
      await gqlRefetch();
    } catch (refetchError) {
      const err = new Error(`Failed to refetch chat history: ${refetchError}`);
      setError(err);
      onErrorRef.current?.(err);
      captureExceptionRef.current?.(err);
    }
  }, [gqlRefetch]);

  return {
    historyData,
    isLoading: loading,
    error,
    refetch,
  };
};
